<template>
  <div class="routes-status">
    <section class="card">
      <div class="toolbar">
        <div class="field field-search">
          <div class="select-area">
            <button class="dropdown-summary" type="button" @click="toggleSearch">
              <span>{{ appliedSearch || 'Все города' }}</span>
              <span class="chevron">{{ searchOpen ? '▲' : '▼' }}</span>
            </button>
            <div v-if="searchOpen" class="dropdown-menu city-menu">
              <input class="input city-search" :value="searchQuery" @input="onSearchInput" placeholder="Поиск города" type="text" />
              <div class="dropdown-hint">{{ searchHint }}</div>
              <div class="options-list">
                <div v-if="appliedSearch" class="dropdown-option clear-option" @click="clearCity">
                  <span class="option-text">← Показать все города</span>
                </div>
                <div v-for="opt in searchOptions" :key="(opt.city_id || '') + opt.city_name" class="dropdown-option" @click="selectCity(opt)">
                  <span class="option-text">{{ opt.city_name }}</span>
                  <span class="count">{{ opt.region_name }}</span>
                </div>
                <div v-if="!searchOptions.length" class="empty-options">Нет вариантов</div>
              </div>
            </div>
          </div>
        </div>
        <div class="toolbar-actions">
          <button class="btn-secondary" :disabled="loading" @click="load">
            {{ loading ? 'Обновляем...' : 'Обновить' }}
          </button>
        </div>
      </div>

      <div v-if="hasError" class="error-message">Не удалось загрузить направления.</div>

      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Направление</th>
              <th>Расстояние</th>
              <th>Бюджет</th>
              <th>Тент (реальная)</th>
              <th>Реф / ISO (расчёт)</th>
              <th>Дата</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && !items.length">
              <td colspan="7" class="muted center">Загрузка...</td>
            </tr>
            <tr v-else-if="!items.length">
              <td colspan="7" class="muted center">Нет данных</td>
            </tr>
            <tr v-for="row in items" :key="row.ati_route_id">
              <td>
                <div class="dir">{{ row.departure_city }} → {{ row.destination_city }}</div>
                <div class="dir-meta">
                  <span class="dir-id">№ {{ row.ati_route_id }}</span>
                  <button class="link-overview" title="Открыть в Обзоре" @click="goOverview(row)">↗ в Обзор</button>
                </div>
              </td>
              <td>{{ row.distance ? row.distance + ' км' : '—' }}</td>
              <td>
                <span v-if="row.is_priority" class="badge badge-priority">priority</span>
                <span v-else-if="isFact(row)" class="badge badge-fact">fact</span>
                <span v-else class="badge badge-muted">—</span>
              </td>
              <td>
                <div v-if="row.price?.tent" class="price-real">
                  <span class="dot dot-real"></span>
                  {{ formatPrice(row.price.tent.avg_volume_with_nds) }} ₽
                </div>
                <span v-else class="muted">—</span>
              </td>
              <td class="derived-cell">
                <span v-if="row.price?.ref"><span class="dot dot-derived"></span>Реф {{ formatPrice(row.price.ref.avg_volume_with_nds) }}</span>
                <span v-if="row.price?.iso" class="iso-line"><span class="dot dot-derived"></span>ISO {{ formatPrice(row.price.iso.avg_volume_with_nds) }}</span>
                <span v-if="!row.price?.ref && !row.price?.iso" class="muted">—</span>
              </td>
              <td>{{ row.price?.tent?.report_date || '—' }}</td>
              <td>
                <button
                  v-if="isFact(row)"
                  class="btn-refresh"
                  :disabled="refreshingId === row.ati_route_id"
                  :title="refreshStatus(row)"
                  @click="refresh(row)"
                >
                  {{ refreshingId === row.ati_route_id ? '...' : '⟳ Обновить' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pager">
        <button class="btn-page" :disabled="page <= 1 || loading" @click="goPage(page - 1)">‹ Назад</button>
        <span class="page-info">Стр. {{ page }} / {{ totalPages }}</span>
        <button class="btn-page" :disabled="page >= totalPages || loading" @click="goPage(page + 1)">Вперёд ›</button>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { createToaster } from '@meforma/vue-toaster'
import { getRoutesWithStatus, refreshCarrierPrice } from '@/backend/atiPriceCheckApi'
import { searchCities } from '@/backend/distributorRoutingApi'
import { useErrorHandler } from '@/hooks/errorHandler'

const fmt = new Intl.NumberFormat('ru-RU')

const POPULAR_CITIES = [
  { city_id: null, city_name: 'Москва', region_name: 'Московская область' },
  { city_id: null, city_name: 'Санкт-Петербург', region_name: 'Ленинградская область' },
  { city_id: null, city_name: 'Казань', region_name: 'Республика Татарстан' },
  { city_id: null, city_name: 'Ульяновск', region_name: 'Ульяновская область' },
  { city_id: null, city_name: 'Екатеринбург', region_name: 'Свердловская область' },
  { city_id: null, city_name: 'Новосибирск', region_name: 'Новосибирская область' },
  { city_id: null, city_name: 'Краснодар', region_name: 'Краснодарский край' },
  { city_id: null, city_name: 'Нижний Новгород', region_name: 'Нижегородская область' },
  { city_id: null, city_name: 'Самара', region_name: 'Самарская область' },
  { city_id: null, city_name: 'Уфа', region_name: 'Республика Башкортостан' },
]

export default {
  name: 'RoutesStatusTable',
  setup() {
    const router = useRouter()
    const toaster = createToaster()

    const appliedSearch = ref('')
    const items = ref([])
    const total = ref(0)
    const page = ref(1)
    const perPage = ref(20)
    const loading = ref(false)
    const hasError = ref(false)
    const refreshingId = ref(null)

    const searchOpen = ref(false)
    const searchQuery = ref('')
    const searchOptions = ref([])
    const searchLoading = ref(false)
    let searchTimer = null

    const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)))

    const formatPrice = (v) => (v == null ? '—' : fmt.format(v))

    const isFact = (row) => !row.is_priority && row.last_refresh?.bucket === 'fact'
    const refreshStatus = (row) => row.last_refresh ? `${row.last_refresh.bucket}: ${row.last_refresh.status}` : ''

    const searchHint = computed(() => {
      if (searchLoading.value) return 'Поиск...'
      if (!searchQuery.value.trim()) return searchOptions.value.length ? 'Показаны популярные города' : 'Начните вводить город'
      return searchOptions.value.length ? 'Показаны первые 30 городов' : 'Ничего не найдено'
    })

    const loadSearchOptions = async (rawQ) => {
      searchLoading.value = true
      try {
        const trimmed = (rawQ || '').trim()
        const data = await searchCities(trimmed, 30)
        let list = Array.isArray(data) ? data : (data && data.items ? data.items : [])
        if (!list.length && !trimmed) list = POPULAR_CITIES
        searchOptions.value = list
      } catch (e) {
        searchOptions.value = (rawQ || '').trim() ? [] : POPULAR_CITIES
      } finally {
        searchLoading.value = false
      }
    }

    const toggleSearch = () => {
      searchOpen.value = !searchOpen.value
      if (searchOpen.value) {
        searchQuery.value = ''
        if (searchOptions.value.length === 0) loadSearchOptions('')
      }
    }

    const onSearchInput = (e) => {
      clearTimeout(searchTimer)
      const val = e.target.value
      searchQuery.value = val
      searchTimer = setTimeout(() => {
        if (!val.trim()) {
          searchOptions.value = []
          return
        }
        loadSearchOptions(val)
      }, 300)
    }

    const selectCity = (opt) => {
      appliedSearch.value = opt.city_name
      searchQuery.value = ''
      searchOptions.value = []
      searchOpen.value = false
      page.value = 1
      load()
    }

    const clearCity = () => {
      appliedSearch.value = ''
      searchQuery.value = ''
      searchOpen.value = false
      page.value = 1
      load()
    }

    const onClickOutside = (e) => {
      if (searchOpen.value && !e.target.closest('.field-search')) searchOpen.value = false
    }

    const load = async () => {
      loading.value = true
      hasError.value = false
      try {
        const data = await getRoutesWithStatus({ search: appliedSearch.value || null, page: page.value, per_page: perPage.value })
        items.value = data.items || []
        total.value = data.total || 0
      } catch (err) {
        hasError.value = true
        useErrorHandler(err)
      } finally {
        loading.value = false
      }
    }

    const goPage = (p) => {
      page.value = p
      load()
    }

    const goOverview = (row) => {
      router.push({
        path: '/ati-price-check',
        query: {
          tab: 'overview',
          from: row.departure_city,
          to: row.destination_city,
          from_region: '',
          to_region: '',
        },
      })
    }

    const refresh = async (row) => {
      if (refreshingId.value) return
      refreshingId.value = row.ati_route_id
      try {
        const res = await refreshCarrierPrice({ ati_route_id: row.ati_route_id })
        if (res.status === 'success') {
          toaster.success(`Цена обновлена: ${res.price ? 'получена' : 'без изменений'}`)
          await load()
        } else if (res.status === 'budget_exhausted') {
          toaster.warning('Бюджет ATI исчерпан')
        } else if (res.status === 'not_found') {
          toaster.info('Маршрут не найден на ATI')
        } else {
          toaster.info(`Статус: ${res.status}`)
        }
      } catch (err) {
        useErrorHandler(err)
      } finally {
        refreshingId.value = null
      }
    }

    onMounted(() => {
      window.addEventListener('click', onClickOutside)
      load()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', onClickOutside)
      clearTimeout(searchTimer)
    })

    return {
      appliedSearch,
      items,
      total,
      page,
      totalPages,
      loading,
      hasError,
      refreshingId,
      formatPrice,
      isFact,
      refreshStatus,
      load,
      goPage,
      refresh,
      goOverview,
      searchOpen,
      searchQuery,
      searchOptions,
      searchHint,
      toggleSearch,
      onSearchInput,
      selectCity,
      clearCity,
    }
  },
}
</script>

<style scoped>
.routes-status {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card {
  padding: 20px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 22px rgba(30, 50, 90, 0.08);
}

.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.field-search {
  flex: 1 1 280px;
  position: relative;
}

.select-area {
  position: relative;
}

.dropdown-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  background: #f8fbff;
  color: #1d2b44;
  font-size: 14px;
  cursor: pointer;
  min-height: 20px;
  box-sizing: border-box;
  gap: 6px;
}

.dropdown-summary:hover {
  border-color: #3f6fb5;
}

.chevron {
  flex-shrink: 0;
  color: #3f6fb5;
  font-size: 12px;
}

.dropdown-menu {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 340px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(30, 50, 90, 0.18);
  user-select: none;
}

.input {
  padding: 8px 10px;
  border: 1px solid #e1e8f1;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  color: #1d2b44;
  box-sizing: border-box;
}

.city-search {
  margin-bottom: 6px;
  box-sizing: border-box;
  width: 100%;
}

.dropdown-hint {
  margin-bottom: 4px;
  color: #9aa6b5;
  font-size: 12px;
  padding: 0 4px;
}

.options-list {
  display: flex;
  flex-direction: column;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.dropdown-option:hover {
  background: #eef3fb;
}

.clear-option .option-text {
  color: #3f6fb5;
  font-weight: 600;
}

.option-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1d2b44;
  font-weight: 500;
}

.count {
  color: #6b7686;
  font-size: 12px;
}

.empty-options {
  color: #9aa6b5;
  font-size: 13px;
  padding: 8px;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.btn-secondary {
  padding: 9px 16px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  text-align: left;
  padding: 10px;
  background: #f4f7fc;
  color: #42506a;
  font-weight: 600;
  border-bottom: 1px solid #e1e8f1;
}

.data-table td {
  padding: 10px;
  border-bottom: 1px solid #eef1f6;
  color: #1d2b44;
  vertical-align: top;
}

.data-table tbody tr:hover {
  background: #f8fbff;
}

.dir {
  font-weight: 600;
}

.dir-id {
  font-size: 12px;
  color: #9aa6b5;
}

.dir-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.link-overview {
  border: none;
  background: transparent;
  color: #2c5e93;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline dotted;
}

.link-overview:hover {
  color: #3f6fb5;
}

.muted {
  color: #9aa6b5;
}

.center {
  text-align: center;
}

.badge {
  padding: 2px 8px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 600;
}

.badge-priority { background: #e7e9fb; color: #3a3f9c; }
.badge-fact { background: #eaf3fb; color: #2c5e93; }
.badge-muted { background: #eef1f6; color: #6b7686; }

.price-real {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #1d7a47;
  font-weight: 600;
}

.derived-cell {
  color: #6b7686;
}

.derived-cell span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.iso-line {
  margin-left: 10px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.dot-real { background: #2d8a57; }
.dot-derived { background: #b6bfcc; }

.btn-refresh {
  padding: 6px 12px;
  border: 1px solid #2c5e93;
  border-radius: 8px;
  background: #eaf3fb;
  color: #2c5e93;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 9px;
  background: #fdecec;
  color: #a52828;
}

.pager {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-top: 14px;
}

.btn-page {
  padding: 6px 14px;
  border: 1px solid #c3ccdb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: #56657a;
  font-size: 14px;
}
</style>
